/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import rcAllenBanner from "@/assets/images/RC_Allen_Banner.png";
import attitudeIndicators from "@/assets/images/Attitude_Indicators.png";
import directionalIndicators from "@/assets/images/Directional_indicators.png";
import turnIndicators from "@/assets/images/Turn_indicators.png";
import batteryBackup from "@/assets/images/Battery_Backup.png";
import tachsAndMore from "@/assets/images/Tachs_and_More.png";
import rca8310BothScreens from "@/assets/images/RCA8310_Both_Screens.jpg";
import rca1510 from "@/assets/images/RCA1510.jpg";
import mini6 from "@/assets/images/Mini6.jpg";
import mini6Logo from "@/assets/images/Mini6_logo.png";
import internalBatteryBackupLogo from "@/assets/images/Internal_Battery_Backup_Logo_small.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // RC Allen Banner/Logo
  "/images/RC_Allen_Banner.png": rcAllenBanner,
  "images/RC_Allen_Banner.png": rcAllenBanner,
  "/assets/images/RC_Allen_Banner.png": rcAllenBanner,
  
  // Navigation Images
  "/images/Attitude_Indicators.png": attitudeIndicators,
  "images/Attitude_Indicators.png": attitudeIndicators,
  "/assets/images/Attitude_Indicators.png": attitudeIndicators,
  
  "/images/Directional_indicators.png": directionalIndicators,
  "images/Directional_indicators.png": directionalIndicators,
  "/assets/images/Directional_indicators.png": directionalIndicators,
  
  "/images/Turn_indicators.png": turnIndicators,
  "images/Turn_indicators.png": turnIndicators,
  "/assets/images/Turn_indicators.png": turnIndicators,
  
  "/images/Battery_Backup.png": batteryBackup,
  "images/Battery_Backup.png": batteryBackup,
  "/assets/images/Battery_Backup.png": batteryBackup,
  
  "/images/Tachs_and_More.png": tachsAndMore,
  "images/Tachs_and_More.png": tachsAndMore,
  "/assets/images/Tachs_and_More.png": tachsAndMore,
  
  // RCA8310 Product Images
  "/images/RCA8310-3 Both Screens.jpg": rca8310BothScreens,
  "images/RCA8310-3 Both Screens.jpg": rca8310BothScreens,
  "/assets/images/RCA8310_Both_Screens.jpg": rca8310BothScreens,
  "/images/RCA8310_Both_Screens.jpg": rca8310BothScreens,
  
  // RCA1510 Product Images
  "/images/RCA1510.jpg": rca1510,
  "images/RCA1510.jpg": rca1510,
  "/assets/images/RCA1510.jpg": rca1510,
  
  // Mini6 Product Images
  "/images/small_pics/Mini 6.jpg": mini6,
  "images/small_pics/Mini 6.jpg": mini6,
  "/images/Mini6.jpg": mini6,
  "images/Mini6.jpg": mini6,
  "/assets/images/Mini6.jpg": mini6,
  
  "/images/Mini6 logo.png": mini6Logo,
  "images/Mini6 logo.png": mini6Logo,
  "/images/Mini6_logo.png": mini6Logo,
  "images/Mini6_logo.png": mini6Logo,
  "/assets/images/Mini6_logo.png": mini6Logo,
  
  "/images/Internal Battery Backup Logo small.jpg": internalBatteryBackupLogo,
  "images/Internal Battery Backup Logo small.jpg": internalBatteryBackupLogo,
  "/images/Internal_Battery_Backup_Logo_small.jpg": internalBatteryBackupLogo,
  "images/Internal_Battery_Backup_Logo_small.jpg": internalBatteryBackupLogo,
  "/assets/images/Internal_Battery_Backup_Logo_small.jpg": internalBatteryBackupLogo,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
